import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AiAgentsAPI from '../../api/aiAgents';
import AiAgentDocumentsAPI from '../../api/aiAgentDocuments';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  documents: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingDocuments: false,
    isCreatingDocument: false,
  },
};

// O index de agent_bots ignora ?type=ai_agent e devolve todos os bots da conta
// mais os globais. Agente de IA é o bot da própria conta salvo pelo construtor,
// que grava bot_config.ai_config (o bot_type é webhook como os outros).
export const isAiAgentBot = record =>
  Boolean(record?.account_id) && Boolean(record?.bot_config?.ai_config);

export const getters = {
  getAiAgents($state) {
    return $state.records.filter(isAiAgentBot);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAiAgent: $state => agentId => {
    return $state.records.find(r => r.id === Number(agentId)) || {};
  },
  getDocuments: $state => agentId => {
    return $state.documents[agentId] || [];
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await AiAgentsAPI.getAiAgents();
      commit(types.SET_AI_AGENTS, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, agentData) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isCreating: true });
    try {
      const response = await AiAgentsAPI.create(agentData);
      commit(types.ADD_AI_AGENT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, data }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AiAgentsAPI.update(id, data);
      commit(types.EDIT_AI_AGENT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isDeleting: true });
    try {
      await AiAgentsAPI.delete(id);
      commit(types.DELETE_AI_AGENT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isDeleting: false });
    }
  },

  fetchDocuments: async ({ commit }, agentBotId) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingDocuments: true });
    try {
      const response = await AiAgentDocumentsAPI.getDocuments(agentBotId);
      commit(types.SET_AI_AGENT_DOCUMENTS, {
        agentId: agentBotId,
        documents: response.data,
      });
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingDocuments: false });
    }
  },

  createDocument: async ({ commit }, { agentBotId, data }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isCreatingDocument: true });
    try {
      const response = await AiAgentDocumentsAPI.createDocument(
        agentBotId,
        data
      );
      commit(types.ADD_AI_AGENT_DOCUMENT, {
        agentId: agentBotId,
        document: response.data,
      });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isCreatingDocument: false });
    }
  },

  deleteDocument: async ({ commit }, { agentBotId, documentId }) => {
    try {
      await AiAgentDocumentsAPI.deleteDocument(agentBotId, documentId);
      commit(types.DELETE_AI_AGENT_DOCUMENT, { agentId: agentBotId, documentId });
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  retryDocument: async ({ commit }, { agentBotId, documentId }) => {
    try {
      const response = await AiAgentDocumentsAPI.retryDocument(
        agentBotId,
        documentId
      );
      commit(types.UPDATE_AI_AGENT_DOCUMENT, {
        agentId: agentBotId,
        document: response.data,
      });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },
};

export const mutations = {
  [types.SET_AI_AGENT_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_AI_AGENTS]: MutationHelpers.set,
  [types.ADD_AI_AGENT]: MutationHelpers.setSingleRecord,
  [types.EDIT_AI_AGENT]: MutationHelpers.update,
  [types.DELETE_AI_AGENT]: MutationHelpers.destroy,
  [types.SET_AI_AGENT_DOCUMENTS]($state, { agentId, documents }) {
    $state.documents = { ...$state.documents, [agentId]: documents };
  },
  [types.ADD_AI_AGENT_DOCUMENT]($state, { agentId, document }) {
    const docs = $state.documents[agentId] || [];
    $state.documents = { ...$state.documents, [agentId]: [document, ...docs] };
  },
  [types.UPDATE_AI_AGENT_DOCUMENT]($state, { agentId, document }) {
    const docs = ($state.documents[agentId] || []).map(d =>
      d.id === document.id ? document : d
    );
    $state.documents = { ...$state.documents, [agentId]: docs };
  },
  [types.DELETE_AI_AGENT_DOCUMENT]($state, { agentId, documentId }) {
    const docs = ($state.documents[agentId] || []).filter(
      d => d.id !== documentId
    );
    $state.documents = { ...$state.documents, [agentId]: docs };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
